import { PAGE_ROLES } from '@bo/constants/userRoles';
import BackOfficeLayout from '@boComponents/BackOfficeLayout';
import { useBoAuth } from '@boContexts/BoAuthContext';
import useClientOverrides from '@boHooks/useClientOverrides';
import useClients from '@boHooks/useClients';
import useCollection from '@boHooks/useCollection';
import useCollectionOptions from '@boHooks/useCollectionOptions';
import useContextData from '@boHooks/useContextData';
import useCsmUsers from '@boHooks/useCsmUsers';
import usePlatforms from '@boHooks/usePlatforms';
import useTest from '@boHooks/useTest';
import useUnsavedChangesBlocker from '@boHooks/useUnsavedChangesBlocker';
import ClientService from '@boServices/clientService';
import CollectionService from '@boServices/collectionService';
import PlatformService from '@boServices/platformService';
import TestService from '@boServices/testService';
import ClientDetail from '@boViews/ClientDetail';
import ClientList, { INITIAL_QUERY, PAGE_SIZE } from '@boViews/ClientList';
import CollectionDetail from '@boViews/CollectionDetail';
import { RESTRICTED } from '@boViews/CollectionDetail/accessRules';
import { rowPayload, splitSettled, targetOf } from '@boViews/CollectionDetail/overrideRules';
import { addTestErrorToast, removeTestErrorToast, RESULT_LIMIT, withAddedTest, withoutTest } from '@boViews/CollectionDetail/testRules';
import CollectionList from '@boViews/Collections/CollectionList';
import ErrorPage from '@boViews/ErrorPage';
import BoLogin from '@boViews/Login';
import NotFound from '@boViews/NotFound';
import Parameters from '@boViews/Parameters';
import PlatformDetail from '@boViews/PlatformDetail';
import BoResetPassword from '@boViews/ResetPassword';
import TagList from '@boViews/Tags/TagList';
import TestDetail from '@boViews/TestDetail';
import TestList from '@boViews/Tests/TestList';
import Users from '@boViews/Users';
import ConfirmModal from '@sharedComponents/ConfirmModal';
import PageLoading from '@sharedComponents/PageLoading';
import { showToast } from '@sharedComponents/Toast';
import { useModal } from '@sharedContexts/ModalContext';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';

const clientService = new ClientService();
const platformService = new PlatformService();
const collectionService = new CollectionService();
const testService = new TestService();

// The add search only offers active clients and platforms, each client with its platforms.
const searchClients = (term, { signal } = {}) =>
    clientService.getClients({ search: term, activeOnly: true, withPlatforms: true, perPage: 20, signal });

// The test search asks for one test more than it shows, to tell whether there are others.
const searchTests = (term, { signal } = {}) => testService.searchTests({ search: term, limit: RESULT_LIMIT + 1, signal });

// The "Sub-clients and platforms" modal only offers the active sub-clients and platforms of a client.
const loadClient = (uuid) => clientService.getClient(uuid, { withPlatforms: true, withSubClients: true, activeOnly: true });

// PUT and DELETE of the override row of a target of the collection `id`: a client, or a platform.
const putOverride = (id, { kind, uuid }, payload) =>
    kind === 'platform' ? collectionService.putPlatformOverride(id, uuid, payload) : collectionService.putClientOverride(id, uuid, payload);
const deleteOverride = (id, { kind, uuid }) =>
    kind === 'platform' ? collectionService.deletePlatformOverride(id, uuid) : collectionService.deleteClientOverride(id, uuid);

function ProtectedRoute({ redirectPath = '/login' }) {
    const { status } = useBoAuth();
    if (status === 'loading') return <PageLoading />;
    if (status === 'unauth') return <Navigate to={redirectPath} replace />;
    return <Outlet />;
}

// Gates a subtree behind a required role, expanded through the BO role hierarchy.
// Runs inside ProtectedRoute, so auth is already settled here; an insufficient role redirects to /clients.
function RoleRoute({ role }) {
    const { hasRole } = useBoAuth();
    if (!hasRole(role)) return <Navigate to="/clients" replace />;
    return <Outlet />;
}

// The two tabs of ClientList share the same search box and page; only the active tab's list is fetched.
function ClientsRoute() {
    const { t } = useTranslation('bo');
    const navigate = useNavigate();
    const { user } = useBoAuth();
    const [query, setQuery] = useState(INITIAL_QUERY);
    const csmUsers = useCsmUsers();

    const isPlatformTab = query.tab === 'platform';

    const {
        clients,
        total: clientTotal,
        loading: clientsLoading,
        refresh: refreshClients,
    } = useClients({
        search: query.search,
        status: query.status,
        category: query.category,
        page: query.page,
        perPage: PAGE_SIZE,
        enabled: !isPlatformTab,
    });

    const {
        platforms,
        total: platformTotal,
        loading: platformsLoading,
    } = usePlatforms({
        search: query.search,
        types: query.types,
        page: query.page,
        perPage: PAGE_SIZE,
        enabled: isPlatformTab,
    });

    const handleCreate = async (payload) => {
        try {
            await clientService.createClient(payload);
            showToast({ type: 'success', text: t('The client has been created.') });
            refreshClients();
        } catch (err) {
            if (err.status !== 409) {
                showToast({ type: 'error', text: t('An error occurred while creating the client. Please try again.') });
            }
            // Re-thrown so the form can map a 409 (duplicate reference) onto its reference field.
            throw err;
        }
    };

    return (
        <ClientList
            items={isPlatformTab ? platforms : clients}
            total={isPlatformTab ? platformTotal : clientTotal}
            loading={isPlatformTab ? platformsLoading : clientsLoading}
            users={csmUsers}
            currentUserUuid={user?.uuid ?? null}
            onQueryChange={setQuery}
            onCreate={handleCreate}
            onView={(uuid) => navigate(`/clients/${uuid}`)}
            onViewPlatform={(clientUuid, platformUuid) => navigate(`/clients/${clientUuid}/platforms/${platformUuid}`)}
        />
    );
}

function ClientDetailRoute() {
    const { t } = useTranslation('bo');
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const csmUsers = useCsmUsers();
    const { actions: modalActions } = useModal();

    useEffect(() => {
        setLoading(true);
        clientService
            .getClient(uuid)
            .then(setClient)
            .catch(() => setClient(null))
            .finally(() => setLoading(false));
    }, [uuid]);

    const { platforms, loading: platformsLoading, refresh: refreshPlatforms } = usePlatforms({ clientId: uuid, page: 1, perPage: 100 });
    const { context, loading: contextLoading, error: contextError, save: saveContext } = useContextData(clientService, uuid);
    // Keyed by uuid so unsaved changes never block the navigation to another client.
    const [dirtyContextOf, setDirtyContextOf] = useState(null);
    const contextDirty = dirtyContextOf === uuid;
    useUnsavedChangesBlocker(contextDirty);

    const saveClient = async (payload) => {
        try {
            const updated = await clientService.updateClient(uuid, payload);
            setClient(updated);
            // The server deactivates the platforms of a deactivated client: reload them.
            if (!updated.active) refreshPlatforms();
            showToast({ type: 'success', text: t('Your changes have been saved.') });
        } catch (err) {
            const text =
                err.status === 409 ? t('This client reference already exists') : t('An error occurred while saving. Please try again.');
            showToast({ type: 'error', text });
        }
    };

    // Deactivating a client also deactivates all of its platforms: confirm before saving.
    // `client?.` because the React Compiler reads this memo dependency on every render, before the
    // loading / not-found guards below, while the client is still null.
    const handleSave = (payload) => {
        if (!client?.active || payload.active) return saveClient(payload);
        return modalActions.open({
            component: ConfirmModal,
            title: t('Deactivate the client'),
            size: 's',
            message: t('Deactivating this client also deactivates all of its platforms. Do you want to continue?'),
            cancelLabel: t('Cancel'),
            confirmLabel: t('Deactivate'),
            confirmTheme: 'danger',
            onCancel: () => modalActions.close(),
            onConfirm: async () => {
                modalActions.close();
                await saveClient(payload);
            },
        });
    };

    const handleCreatePlatform = async (payload) => {
        try {
            await platformService.createPlatform({ ...payload, clientId: uuid });
            showToast({ type: 'success', text: t('The platform has been created.') });
            refreshPlatforms();
        } catch (err) {
            if (err.status !== 409) {
                showToast({ type: 'error', text: t('An error occurred while creating the platform. Please try again.') });
            }
            // Re-thrown so the form can map a 409 (duplicate URL) onto its URL field.
            throw err;
        }
    };

    if (loading) return <PageLoading />;
    if (!client) return <Navigate to="/clients" replace />;

    return (
        <ClientDetail
            client={client}
            csmUsers={csmUsers}
            platforms={platforms}
            platformsLoading={platformsLoading}
            onBack={() => navigate('/clients')}
            onSave={handleSave}
            onCreatePlatform={handleCreatePlatform}
            onViewPlatform={(platformUuid) => navigate(`/clients/${uuid}/platforms/${platformUuid}`)}
            context={context}
            contextLoading={contextLoading}
            contextError={contextError}
            onSaveContext={saveContext}
            onContextDirtyChange={(dirty) => setDirtyContextOf(dirty ? uuid : null)}
        />
    );
}

function PlatformDetailRoute() {
    const { t } = useTranslation('bo');
    const { uuid, platformUuid } = useParams();
    const navigate = useNavigate();
    const [platform, setPlatform] = useState(null);
    const [loading, setLoading] = useState(true);
    const { context, loading: contextLoading, error: contextError, save: saveContext } = useContextData(platformService, platformUuid);
    // A failure to load the client's context only hides the read-only client block (intentional degradation).
    const { context: clientContext } = useContextData(clientService, uuid);
    // Keyed by uuid so unsaved changes never block the navigation to another platform.
    const [dirtyContextOf, setDirtyContextOf] = useState(null);
    const contextDirty = dirtyContextOf === platformUuid;
    useUnsavedChangesBlocker(contextDirty);

    useEffect(() => {
        setLoading(true);
        platformService
            .getPlatform(platformUuid)
            .then(setPlatform)
            .catch(() => setPlatform(null))
            .finally(() => setLoading(false));
    }, [platformUuid]);

    const handleSave = async (payload) => {
        try {
            const updated = await platformService.updatePlatform(platformUuid, payload);
            setPlatform(updated);
            showToast({ type: 'success', text: t('Your changes have been saved.') });
            return updated;
        } catch (err) {
            const text = err.status === 409 ? t('This URL is already used') : t('An error occurred while saving. Please try again.');
            showToast({ type: 'error', text });
            return null;
        }
    };

    if (loading) return <PageLoading />;
    if (!platform) return <Navigate to={`/clients/${uuid}`} replace />;

    return (
        <PlatformDetail
            initialPlatform={platform}
            context={context}
            contextLoading={contextLoading}
            contextError={contextError}
            clientContext={clientContext}
            onBack={() => navigate(`/clients/${uuid}`)}
            onSave={handleSave}
            onSaveContext={saveContext}
            onContextDirtyChange={(dirty) => setDirtyContextOf(dirty ? platformUuid : null)}
            onOpenClient={() => navigate(`/clients/${uuid}`)}
        />
    );
}

function CollectionDetailRoute() {
    const { t } = useTranslation('bo');
    const { id } = useParams();
    const navigate = useNavigate();
    const { actions: modalActions } = useModal();
    const { collection, loading, error, setCollection } = useCollection(id);
    const { tagsOptions, languagesOptions, levelsOptions, loading: optionsLoading } = useCollectionOptions();
    const { overrides, loading: overridesLoading, error: overridesError, reload: reloadOverrides } = useClientOverrides(id);
    const [dirty, setDirty] = useState(false);
    // Removals of tests in flight, each with the id of its collection: a removal on one collection does not disable
    // the bin of the same test on another.
    const [pendingRemovals, setPendingRemovals] = useState([]);
    const pendingTestReferences = pendingRemovals.filter((removal) => removal.id === id).map((removal) => removal.reference);
    // Set after a deletion: the page then leaves without asking about unsaved changes.
    const [redirectTo, setRedirectTo] = useState(null);
    useUnsavedChangesBlocker(dirty && redirectTo === null);

    useEffect(() => {
        if (redirectTo) navigate(redirectTo, { replace: true });
    }, [redirectTo, navigate]);

    const handleSave = async (payload) => {
        try {
            const updated = await collectionService.updateCollection(id, payload);
            setCollection(updated);
            showToast({ type: 'success', text: t('Your changes have been saved.') });
            return updated;
        } catch {
            showToast({ type: 'error', text: t('An error occurred while saving. Please try again.') });
            return null;
        }
    };

    // Never rejects: AccessSection calls it without waiting for the outcome.
    const handleChangeVisibility = async (visibility) => {
        try {
            const updated = await collectionService.changeVisibility(id, visibility);
            setCollection(updated);
            reloadOverrides();
            showToast({ type: 'success', text: t('The scope of the collection has been changed.') });
        } catch {
            showToast({ type: 'error', text: t('An error occurred while changing the scope. Please try again.') });
        }
    };

    // Never rejects: TargetSearch keeps the "+" of the target disabled until it settles.
    const handleAddTarget = async ({ kind, uuid, name }) => {
        // Computed outside the try: the React Compiler does not support conditional expressions within a try/catch.
        // Optional chaining: the compiler reads this dependency at render time, while the collection is still loading.
        const successText =
            collection?.visibility === RESTRICTED
                ? t('{{name}} is now authorized.', { name })
                : t('{{name}} has been added to the list.', { name });
        try {
            await putOverride(id, { kind, uuid });
            await reloadOverrides();
            showToast({ type: 'success', text: successText });
        } catch {
            showToast({ type: 'error', text: t('An error occurred while adding. Please try again.') });
        }
    };

    // Sends `request`, then reloads the rows and shows `successText`, already translated. Never rejects: resolves to
    // whether it succeeded, AccessSection keeping the buttons of the rows disabled until then.
    const runRowChange = async (request, successText) => {
        try {
            await request();
            await reloadOverrides();
            showToast({ type: 'success', text: successText });
            return true;
        } catch {
            showToast({ type: 'error', text: t('An error occurred while saving. Please try again.') });
            return false;
        }
    };

    const handleSaveOverride = (row, changes, successText) =>
        runRowChange(() => putOverride(id, targetOf(row), rowPayload(row, changes)), successText);
    const handleRemoveOverride = (row, successText) => runRowChange(() => deleteOverride(id, targetOf(row)), successText);
    // Moves the platform row `row` to the client `target`.
    const handleTransferOverride = (row, target, successText) =>
        runRowChange(() => collectionService.transferPlatformOverride(id, row.platform.uuid, target.uuid), successText);

    // Never rejects: SubTargetsModal keeps the targets that failed. The requests are sent together, the lock of the
    // collection serializes them on the server; the rows are then reloaded once.
    const handleAddTargets = async (targets, payload) => {
        const outcomes = await Promise.allSettled(targets.map((target) => putOverride(id, target, payload)));
        await reloadOverrides();
        const { added, failed } = splitSettled(targets, outcomes);
        if (failed.length === 0) {
            showToast({ type: 'success', text: t('{{count}} row(s) added.', { count: added.length }) });
        } else {
            showToast({ type: 'error', text: t('Some rows could not be added.') });
        }
        return { added, failed };
    };

    const handlePublish = async () => {
        if (dirty) {
            showToast({ type: 'warning', text: t('Save your changes before publishing.') });
            return;
        }
        try {
            await collectionService.publishCollection(id);
            showToast({ type: 'success', text: t('The collection has been published.') });
        } catch (err) {
            showToast({
                type: 'error',
                text:
                    err.status === 422
                        ? t('This collection cannot be published: it must be public and contain at least one test.')
                        : t('An error occurred while publishing. Please try again.'),
            });
        }
    };

    // Shows the test of `reference` as the server holds it; a failed read leaves the tests as they are.
    const showExistingTest = (reference) =>
        testService.getTest(reference).then(
            (test) => setCollection((current) => withAddedTest(current, test)),
            () => {},
        );

    // Never rejects: TestSearch keeps the "+" of the test disabled until it settles. The added test joins the shown
    // collection, which is not reloaded, to keep the unsaved edits of the Information tab. A 409 means the test was
    // added elsewhere since the page loaded: it is then read and shown.
    const handleAddTest = async ({ reference }) => {
        try {
            const added = await collectionService.addTest(id, reference);
            setCollection((current) => withAddedTest(current, added));
            showToast({ type: 'success', text: t('{{reference}} has been added to the collection.', { reference }) });
        } catch (err) {
            showToast(addTestErrorToast(t, err));
            if (err.status === 409) await showExistingTest(reference);
        }
    };

    // Creates the test, then adds it to the collection. Rejects only when the creation fails, so that the panel stays
    // open: once the test exists it resolves, even if the addition failed, the search then showing the test with its "+".
    const handleCreateTest = async (payload) => {
        let created;
        try {
            created = await testService.createTest(payload);
        } catch (err) {
            if (err.status !== 409) {
                showToast({ type: 'error', text: t('An error occurred while creating the test. Please try again.') });
            }
            throw err;
        }
        const { reference } = created;
        try {
            const added = await collectionService.addTest(id, reference);
            setCollection((current) => withAddedTest(current, added));
            showToast({ type: 'success', text: t('Test {{reference}} created and added to the collection.', { reference }) });
        } catch {
            showToast({
                type: 'warning',
                text: t('Test {{reference}} was created but could not be added to the collection.', { reference }),
            });
        }
    };

    // Never rejects: the bin of the test stays disabled until it settles. A 404 means the test had already left the
    // collection elsewhere: its row is dropped too.
    const handleRemoveTest = async ({ reference }) => {
        const removal = { id, reference };
        setPendingRemovals((current) => [...current, removal]);
        try {
            await collectionService.removeTest(id, reference);
            setCollection((current) => withoutTest(current, reference));
            showToast({ type: 'success', text: t('{{reference}} has been removed from the collection.', { reference }) });
        } catch (err) {
            if (err.status === 404) setCollection((current) => withoutTest(current, reference));
            showToast(removeTestErrorToast(t, err, reference));
        }
        setPendingRemovals((current) => current.filter((pending) => pending !== removal));
    };

    const handleDelete = () =>
        modalActions.open({
            component: ConfirmModal,
            title: t('Delete the collection'),
            size: 's',
            message: t('This collection will be permanently deleted. Do you want to continue?'),
            cancelLabel: t('Cancel'),
            confirmLabel: t('Delete'),
            confirmTheme: 'danger',
            onCancel: () => modalActions.close(),
            onConfirm: async () => {
                modalActions.close();
                try {
                    await collectionService.deleteCollection(id);
                    showToast({ type: 'success', text: t('The collection has been deleted.') });
                    setRedirectTo('/collections');
                } catch {
                    showToast({ type: 'error', text: t('An error occurred while deleting. Please try again.') });
                }
            },
        });

    if (loading || optionsLoading || overridesLoading) return <PageLoading />;
    if (error || overridesError) return <Navigate to="/collections" replace />;

    return (
        <CollectionDetail
            key={collection.id}
            collection={collection}
            languages={languagesOptions}
            levels={levelsOptions}
            tagsOptions={tagsOptions}
            overrides={overrides}
            onSave={handleSave}
            onChangeVisibility={handleChangeVisibility}
            searchClients={searchClients}
            onAddTarget={handleAddTarget}
            onSaveOverride={handleSaveOverride}
            onRemoveOverride={handleRemoveOverride}
            loadClient={loadClient}
            onAddTargets={handleAddTargets}
            onTransferOverride={handleTransferOverride}
            onPublish={handlePublish}
            onSearchTests={searchTests}
            onAddTest={handleAddTest}
            onCreateTest={handleCreateTest}
            onRemoveTest={handleRemoveTest}
            pendingTestReferences={pendingTestReferences}
            onDelete={handleDelete}
            onBack={() => navigate('/collections')}
            onDirtyChange={setDirty}
        />
    );
}

function TestDetailRoute() {
    const { t } = useTranslation('bo');
    const { reference } = useParams();
    const navigate = useNavigate();
    const { test, loading, error, setTest } = useTest(reference);
    const { languagesOptions, levelsOptions, loading: optionsLoading } = useCollectionOptions();
    // Keyed by reference so unsaved changes never block the navigation to another test.
    const [dirtyTestOf, setDirtyTestOf] = useState(null);
    useUnsavedChangesBlocker(dirtyTestOf === reference);

    const handleSave = async (payload) => {
        try {
            const updated = await testService.updateTest(reference, payload);
            setTest(updated);
            showToast({ type: 'success', text: t('Your changes have been saved.') });
            return updated;
        } catch {
            showToast({ type: 'error', text: t('An error occurred while saving. Please try again.') });
            return null;
        }
    };

    if (loading || optionsLoading) return <PageLoading />;
    if (error) return <Navigate to="/tests" replace />;

    return (
        <TestDetail
            key={test.reference}
            test={test}
            languages={languagesOptions}
            levels={levelsOptions}
            onSave={handleSave}
            onOpenCollection={(collection) => navigate(`/collections/${collection.id}`)}
            onBack={() => navigate('/tests')}
            onDirtyChange={(dirty) => setDirtyTestOf(dirty ? reference : null)}
        />
    );
}

const router = createBrowserRouter(
    [
        {
            // Last-resort fallback (login pages, layout itself): the error page replaces the whole screen.
            errorElement: <ErrorPage />,
            children: [
                { path: 'login', element: <BoLogin /> },
                { path: 'reset-password', element: <BoResetPassword /> },
                {
                    element: <ProtectedRoute />,
                    children: [
                        {
                            path: '/',
                            // element is the layout, it will always be displayed event if the path is wrong
                            element: <BackOfficeLayout />,
                            children: [
                                {
                                    // A page that throws is replaced by the error page inside the layout, keeping the menu.
                                    errorElement: <ErrorPage />,
                                    children: [
                                        { index: true, element: <Navigate to="/clients" replace /> },
                                        { path: 'clients', element: <ClientsRoute /> },
                                        { path: 'clients/:uuid', element: <ClientDetailRoute /> },
                                        { path: 'clients/:uuid/platforms/:platformUuid', element: <PlatformDetailRoute /> },
                                        {
                                            element: <RoleRoute role={PAGE_ROLES.collections} />,
                                            children: [
                                                { path: 'collections', element: <CollectionList /> },
                                                { path: 'collections/:id', element: <CollectionDetailRoute /> },
                                                { path: 'tags', element: <TagList /> },
                                            ],
                                        },
                                        {
                                            element: <RoleRoute role={PAGE_ROLES.tests} />,
                                            children: [
                                                { path: 'tests', element: <TestList /> },
                                                { path: 'tests/:reference', element: <TestDetailRoute /> },
                                            ],
                                        },
                                        {
                                            element: <RoleRoute role={PAGE_ROLES.users} />,
                                            children: [{ path: 'users', element: <Users /> }],
                                        },
                                        { path: 'parameters', element: <Parameters /> },
                                        { path: '*', element: <NotFound /> },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
    { basename: import.meta.env.BASE_URL },
);

export default router;
